export interface HealthResponse {
  status: string;
  timestamp: string;
  environment: string;
}

import { authService } from '../services/auth.service';

export interface HealthResponse {
  status: string;
  timestamp: string;
  environment: string;
}

export interface HelloResponse {
  message: string;
  version: string;
}

// Use relative URLs in production (when served through nginx), or use env var if set
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

let commonHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
};

/**
 * Builds the same header set (common headers + JWT auth) every request uses.
 * Shared by `request` (buffered JSON) and `streamNdjson` (streamed) so both
 * paths authenticate/target-environment identically.
 */
const buildRequestHeaders = (url: string, extra?: Record<string, string>): Record<string, string> => {
  const headers: Record<string, string> = {
    ...commonHeaders,
    ...extra,
  };

  // Get token using authService (handles expiration and format correctly)
  // Fallback to localStorage if authService is not available (shouldn't happen)
  let accessToken: string | null = null;
  try {
    accessToken = authService.getAccessToken();
  } catch (error) {
    console.warn('Failed to get token from authService, trying localStorage:', error);
    // Fallback: try to get from localStorage directly
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const tokenData = JSON.parse(token);
        accessToken = tokenData.accessToken || null;
      } catch {
        // If token is not JSON, it might be the access token itself
        accessToken = token;
      }
    }
  }

  // Add Authorization header if token exists
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  } else if (import.meta.env.DEV) {
    // Only log in development to avoid exposing tokens in production
    console.warn(`[API] No access token found for request to ${url}`);
  }

  return headers;
};

/**
 * Shared 401 handling: clears auth state and redirects to /login. Used by
 * both `request` (buffered JSON) and `streamNdjson` (streamed) so a token
 * expiring mid-stream logs the user out the same way a buffered call would,
 * instead of just surfacing a generic stream error.
 */
const handleUnauthorized = (): never => {
  console.error('Unauthorized access - redirecting to login');

  // Clear auth data
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user_info');

  // Redirect to login page
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }

  throw new Error('Unauthorized - Please login again');
};

/**
 * API Request Interceptor
 * Automatically adds JWT token to all requests and handles authentication errors
 */
const request = async <T>(url: string, options?: RequestInit): Promise<T> => {
  const headers = buildRequestHeaders(url, options?.headers as Record<string, string>);

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized - Token expired or invalid
  if (response.status === 401) {
    handleUnauthorized();
  }

  if (!response.ok) {
    // Try to extract error message from response
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorData = await response.json();
      // Check various error message locations
      if (errorData?.error?.message) {
        errorMessage = errorData.error.message;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (errorData?.error) {
        // If error is a string
        errorMessage = typeof errorData.error === 'string' 
          ? errorData.error 
          : errorMessage;
      }
    } catch {
      // If response is not JSON, use status text
      errorMessage = response.statusText || errorMessage;
    }
    
    const error = new Error(errorMessage) as Error & { status?: number; statusText?: string; data?: any };
    error.status = response.status;
    error.statusText = response.statusText;
    
    try {
      const errorData = await response.clone().json();
      error.data = errorData;
    } catch {
      // Response is not JSON, ignore
    }
    
    throw error;
  }

  return response.json();
};

export const apiService = {
  getHealth: (): Promise<HealthResponse> => {
    return request<HealthResponse>('/api/health');
  },

  getHello: (): Promise<HelloResponse> => {
    return request<HelloResponse>('/api/hello');
  },

  get: <T>(url: string, signal?: AbortSignal): Promise<T> => {
    return request<T>(url, { signal });
  },

  post: <T>(url: string, data?: any): Promise<T> => {
    return request<T>(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  put: <T>(url: string, data?: any): Promise<T> => {
    return request<T>(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: <T>(url: string): Promise<T> => {
    return request<T>(url, {
      method: 'DELETE',
    });
  },

  patch: <T>(url: string, data?: any): Promise<T> => {
    return request<T>(url, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

/**
 * Streams a newline-delimited-JSON (NDJSON) GET response, invoking `onItem`
 * for each parsed line as soon as it arrives on the wire -- used for
 * progressive per-table results (e.g. data hygiene) instead of waiting for
 * one large buffered JSON response. Falls back to parsing the whole body at
 * once if the runtime has no streaming `Response.body` support.
 */
export const streamNdjson = async <T>(
  url: string,
  onItem: (item: T) => void,
  signal?: AbortSignal
): Promise<void> => {
  const headers = buildRequestHeaders(url);
  delete headers['Content-Type'];

  const response = await fetch(`${API_BASE_URL}${url}`, { headers, signal });

  if (response.status === 401) {
    handleUnauthorized();
  }

  if (!response.ok) {
    let message = `HTTP error! status: ${response.status}`;
    try {
      const data = await response.json();
      message = data?.error?.message || data?.message || message;
    } catch {
      // ignore, use default message
    }
    throw new Error(message);
  }

  const parseLine = (line: string) => {
    const trimmed = line.trim();
    if (trimmed) onItem(JSON.parse(trimmed));
  };

  if (!response.body || !response.body.getReader) {
    const text = await response.text();
    text.split('\n').forEach(parseLine);
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let newlineIndex: number;
    while ((newlineIndex = buffer.indexOf('\n')) >= 0) {
      parseLine(buffer.slice(0, newlineIndex));
      buffer = buffer.slice(newlineIndex + 1);
    }
  }
  parseLine(buffer);
};

export const setAuthToken = (token: string | null) => {
  if (token) {
    commonHeaders['Authorization'] = `Bearer ${token}`;
  } else {
    delete commonHeaders['Authorization'];
  }
};

export const setCustomHeader = (key: string, value: string) => {
  commonHeaders[key] = value;
};